import React, { useState, useRef, useEffect } from "react";
import { View, StyleSheet, Pressable, GestureResponderEvent, Modal, ActivityIndicator } from "react-native";
import { Platform } from "react-native";
import { useMicrophoneDevices } from "../../hooks/useMicrophoneDevices";
import { saveMicrophonePreference, setSystemDefaultMicrophone } from "../../utils/microphoneStorage";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import AssessmentAudioVisualizer from "./AssessmentAudioVisualizer";
import { Microphone, CheckCircle, CaretDown } from "@phosphor-icons/react";
import { DownArrow } from "../../components/svgs/common";

interface AssessmentMicrophoneSelectorProps {
  selectedDeviceId: string;
  onDeviceSelect: (deviceId: string) => void;
  disabled?: boolean;
}

const AssessmentMicrophoneSelector: React.FC<AssessmentMicrophoneSelectorProps> = ({
  selectedDeviceId,
  onDeviceSelect,
  disabled = false,
}) => {
  const { devices, selectedDeviceId: hookSelectedDeviceId, isLoading, error, selectDevice } = useMicrophoneDevices();
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isAudioActive, setIsAudioActive] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const buttonRef = useRef<View>(null);
  const isMountedRef = useRef(false);

  const effectiveSelectedDeviceId = hookSelectedDeviceId || selectedDeviceId;

  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  const handleDeviceSelect = async (deviceId: string) => {
    setIsSettingDefault(true);
    setMenuVisible(false);
    try {
      await selectDevice(deviceId);
      await saveMicrophonePreference(deviceId);
      await setSystemDefaultMicrophone(deviceId);

      if (typeof onDeviceSelect === "function") {
        onDeviceSelect(deviceId);
      }
      setIsAudioActive(true);
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleOpenMenu = (event: GestureResponderEvent) => {
    if (disabled || isSettingDefault) return;
    setMenuVisible(true);
  };

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  useEffect(() => {
    if (effectiveSelectedDeviceId && devices.length > 0 && !isSettingDefault) {
      // Small delay to ensure the device is properly set
      const timer = setTimeout(() => {
        setIsAudioActive(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [effectiveSelectedDeviceId, devices.length, isSettingDefault]);

  useEffect(() => {
    if (
      isMountedRef.current &&
      hookSelectedDeviceId &&
      hookSelectedDeviceId !== selectedDeviceId &&
      typeof onDeviceSelect === "function" &&
      hookSelectedDeviceId !== "default"
    ) {
      console.log("Syncing hook selected device with parent:", hookSelectedDeviceId);
      onDeviceSelect(hookSelectedDeviceId);
    }
  }, [hookSelectedDeviceId, selectedDeviceId, onDeviceSelect]);

  if (Platform.OS !== "web") {
    return null;
  }

  if (isLoading) {
    return (
      <View style={styles.container}>
        <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
        <CustomSpacing type="horizontal" size="s" />
        <ActivityIndicator size="small" color="#344054" />
        <CustomText text="Loading microphones" size="s" weight="normal" style={{ color: "#667085" }} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
        <CustomSpacing type="horizontal" size="s" />
        <CustomText text={error} size="s" weight="normal" style={{ color: "#F04438" }} useTranslationText={false} />
      </View>
    );
  }

  if (devices.length === 0) {
    return (
      <View style={styles.container}>
        <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
        <CustomSpacing type="horizontal" size="s" />
        <CustomText text="No microphones found" size="s" weight="normal" style={{ color: "#667085" }} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
      <CustomText
        text="Choose your microphone below and check that the moving bars respond when you speak. If there's no activity, check your browser's microphone permissions."
        weight="normal"
        style={{ color: "#344054" }}
      />
      <View style={styles.dropdownContainer}>
        <Pressable
          ref={buttonRef}
          style={[styles.dropdown, disabled && styles.dropdownDisabled]}
          onPress={handleOpenMenu}
          disabled={disabled || isSettingDefault}>
          {isSettingDefault ? (
            <View style={styles.dropdownButton}>
              <ActivityIndicator size="small" color="#344054" />
            </View>
          ) : (
            <View style={styles.dropdownButtonContainer}>
              <Microphone fill="#000" weight="bold" />
              <AssessmentAudioVisualizer deviceId={effectiveSelectedDeviceId} isActive={isAudioActive} />
              <CaretDown fill="#000" weight="bold" size={16} />
            </View>
          )}
        </Pressable>
      </View>

      <Modal
        visible={menuVisible}
        transparent={true}
        animationType="none"
        onRequestClose={handleCloseMenu}
        statusBarTranslucent={false}>
        <Pressable style={styles.modalOverlay} onPress={handleCloseMenu}>
          <View style={styles.customMenu}>
            {devices.map((device) => {
              const isSelected = device.deviceId === effectiveSelectedDeviceId;
              return (
                <Pressable
                  key={device.deviceId}
                  style={styles.menuItem}
                  onPress={() => handleDeviceSelect(device.deviceId)}>
                  <CheckCircle size={24} weight="fill" color={isSelected ? "#4BA30D" : "#D0D5DD"} />
                  <CustomText
                    text={device.label}
                    size="s"
                    style={styles.menuItemText}
                    weight="normal"
                    useTranslationText={false}
                  />
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 18,
  },
  dropdownContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dropdown: {
    backgroundColor: "#F2F4F7",
    borderRadius: 360,
    borderWidth: 0,
    height: 32,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  dropdownDisabled: {
    backgroundColor: "#F2F4F7",
    opacity: 0.6,
  },
  dropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  dropdownButtonContainer: {
    paddingHorizontal: 0,
    paddingLeft: 4,
    paddingRight: 3,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
  },
  customMenu: {
    backgroundColor: "#fff",
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingBottom: 10,
    paddingTop: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
    minWidth: 200,
    maxWidth: 400,
    marginTop: 250,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 10,
  },
  menuItemText: {
    color: "#344054",
  },
});

export default AssessmentMicrophoneSelector;
